import { useState, useEffect, useCallback, useMemo } from "react";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import { FormikTextField, FormikRichTextField, FormikSelectField } from "../../../MUI5FormikComponents/FormikWrappers";
import { Button, ButtonGroup, Grid, Typography } from "@mui/material";
import { IAirport } from "@vmosolutions/oc-interfaces";
import * as Yup from "yup";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { airportIATAValidation, airportICAOValidation } from "../../Validation/validation";
import { findAirports } from "src/utils/airportHelper";
import moment from "moment-timezone";
import { CustomFieldsV2 } from "src/components/Custom/CustomFieldsV2";
import { PolicyResource, IAirportGate } from "@vmosolutions/oc-interfaces";
import _ from "lodash";
import GateManagement from "./GateManagement";
import { AirportGendecSection } from "./AirportGendecSection";

interface IAirportFormProps {
  id: string;
  selectedAirport: IAirport | undefined;
  updateAirportsList: (updatedAP: IAirport, mode: "create" | "update" | "delete" | "showinlist") => void;
  className?: string;
  setActiveModal: (activeModal: IActiveModal) => void;
  loadedAirports: IAirport[];
}
type AirportFormType = FormikProps<IAirport>;

const defaultAirportValues: IAirport = {
  _id: undefined,
  Name: "",
  Ident: "",
  IATACode: undefined,
  Loc: {
    type: "Point",
    coordinates: [],
  },
  timezoneId: "",
  CountryCode: "",
  custom: {},
  createdAt: 0,
  createdBy: "",
  gates: [],
  defaultTaxiOutMinutes: null,
  defaultTaxiInMinutes: null,
};

// Empty input → null so the per-airport override is cleared; otherwise coerce to a number.
const parseTaxiMinutes = (value: string): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const templateHtml =
  "<p><strong>Airport Facilities</strong></p><p><strong>Airport Ops</strong>:<br><strong>Tower</strong>:<br><strong>Flt. Plan</strong>:<br><strong>De-Icing</strong>:<br><strong>Fuel</strong>:<br><strong>Phone</strong>:<br><strong>Fax</strong>:<br><strong>RCR</strong>:<br><strong>Customs</strong>:<br><strong>Alt #</strong>:<br></p>";

const AirportSchema = Yup.object().shape({
  Name: Yup.string().required("Name is required"),
  Ident: Yup.string()
    .required("ICAO code is required")
    .matches(airportICAOValidation, "ICAO code must contain 4 characters"),
  IATACode: Yup.string().matches(airportIATAValidation, "IATA code must contain 3 letters"),
  timezoneId: Yup.string().required("Airport Timezone Id is required"),
  Loc: Yup.object().shape({
    type: Yup.string().required("Location type is required"),
    coordinates: Yup.array(),
    "coordinates[1]": Yup.number().test("testLat", "Latitude (decimal degree) is required", function (value) {
      return this.path === "Loc.coordinates[1]" && !!this.parent.coordinates[1];
    }),
    "coordinates[0]": Yup.number().test("testLong", "Longitude (decimal degree) is required", function (value) {
      return this.path === "Loc.coordinates[0]" && !!this.parent.coordinates[0];
    }),
  }),
  CountryCode: Yup.string().required("Airport Country Code is required"),
  ElevationFeet: Yup.number(),
  FIR: Yup.string(),
  defaultTaxiOutMinutes: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" || originalValue === null ? null : value))
    .min(0, "Must be 0 or greater")
    .typeError("Must be a number"),
  defaultTaxiInMinutes: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" || originalValue === null ? null : value))
    .min(0, "Must be 0 or greater")
    .typeError("Must be a number"),
  note: Yup.string(),
});

const checkForAirport = async (searchString: string, type: "Ident" | "IATACode"): Promise<IAirport | undefined> => {
  const response: IAirport[] = await findAirports(searchString);
  if (Array.isArray(response)) {
    const airportFound = response.find((ac) => ac[type] === searchString);
    return airportFound;
  }
  return undefined;
};

export const AirportForm = (props: IAirportFormProps): JSX.Element => {
  const { id, selectedAirport, updateAirportsList, className, loadedAirports } = props; //setActiveModal
  const dispatch = useDispatch();

  const customSchema = useSelector((state: IRootState) => state.customPages?.airport);
  const schema = useMemo(() => {
    try {
      const data = customSchema ? JSON.parse(customSchema) : null;
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [customSchema]);

  const customPath = "custom";
  const initialCustomValues = useMemo(() => {
    const values: { [key: string]: any } = {};
    if (!schema || !schema.hasOwnProperty("properties") || !selectedAirport) {
      return values;
    }
    Object.keys(schema.properties).forEach((value: string) => {
      values[value] = schema.properties[value].default ?? schema.properties[value]?.selectorProps?.defaultValue;
    });
    return values;
  }, [schema, selectedAirport]);

  const initialValues = useMemo(
    () => _.merge({ [customPath]: initialCustomValues }, { ...defaultAirportValues, ...selectedAirport }),
    [selectedAirport, initialCustomValues, customPath]
  );

  const [ICAOMatch, setICAOMatch] = useState<string>("");
  const [IATAMatch, setIATAMatch] = useState<string>("");
  const [showGateModal, setShowGateModal] = useState<boolean>(false);

  const timezoneOptions = useMemo(() => {
    const timeZones: string[] = moment.tz.names();
    return timeZones.map((name: string) => ({ label: name, value: name }));
  }, []);

  const mosaicPane = useMemo(() => document.getElementById(`${id}-panel`), [id]);
  useEffect(() => {
    setICAOMatch("");
    setIATAMatch("");
  }, [initialValues]);

  const airportFoundMessaging = useCallback(
    (airportFound: IAirport, type: "Ident" | "IATACode") => {
      if (airportFound) {
        const airportCodeString = type === "Ident" ? `ICAO "${airportFound[type]}"` : `IATA "${airportFound[type]}"`;
        const warning = `Airport with ${airportCodeString} found, use menu to the left to load it.`;
        updateAirportsList(airportFound, "showinlist");
        if (type === "Ident") {
          setICAOMatch(warning);
        } else {
          setIATAMatch(warning);
        }
      } else {
        setICAOMatch("");
        setIATAMatch("");
      }
    },
    [updateAirportsList]
  );

  const handleCheckForAirports = useCallback(
    (type: "Ident" | "IATACode", code: string): void => {
      const searchCode = code.toUpperCase();
      const airportFound = loadedAirports?.find((airport) => {
        return airport[type] === searchCode;
      });

      if (airportFound) {
        airportFoundMessaging(airportFound, type);
      } else {
        const searchDatabase = async (code: string): Promise<void> => {
          const airportFound = await checkForAirport(searchCode, type);
          if (airportFound) airportFoundMessaging(airportFound, type);
        };
        searchDatabase(searchCode);
      }
    },
    [airportFoundMessaging, loadedAirports]
  );

  const handleSubmit = async (values: IAirport, helpers: FormikHelpers<IAirport>): Promise<void> => {
    const { setSubmitting, setStatus, resetForm } = helpers;
    const mode = values._id && values.custom ? "update" : "create";
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${mode === "update" ? "Updating" : "Creating New"} Airport...`,
      successMessage: `Airport Successfully ${mode === "update" ? "Updated" : "Created"}.`,
      errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} Airport`,
    };
    const payload: IAirport = {
      ...values,
      Loc: { ...values.Loc, type: "Point" },
      userModified: true,
    };
    const response =
      mode === "create"
        ? await AmplifyAPIWrapper.Post<IAirport>("/airports", payload, notifications)
        : await AmplifyAPIWrapper.Put<IAirport>(`/airports/${values._id}`, payload, notifications);
    if (response?._id) {
      const newValues = { ...response, userModified: true };
      updateAirportsList(newValues, mode);
      resetForm({ values: newValues });
      setStatus({ success: true });
    } else {
      setStatus({ success: false });
    }
    setSubmitting(false);
  };

  const openAirportInfoWindow = (values: IAirport): void => {
    const windowProps = { airportInfo: values };
    dispatch(openWindow("Airport Info", "AirportInfoWindow", [], windowProps, "context"));
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={AirportSchema}
    >
      {(airportForm: AirportFormType) => {
        const { values, dirty, isValid, isSubmitting, handleReset } = airportForm;
        return (
          <Form>
            <Grid container item xs={12}>
              {ICAOMatch && (
                <Typography variant="body2" style={{ color: "orange" }}>
                  Warning: {ICAOMatch}
                </Typography>
              )}
              {IATAMatch && (
                <Typography variant="body2" style={{ color: "orange" }}>
                  Warning: {IATAMatch}
                </Typography>
              )}
            </Grid>

            <Grid container spacing={4}>
              <Grid item xs={6}>
                <FormikTextField className="field textfield airport name" label="Name" name="Name" />
                <FormikTextField
                  className="field textfield airport code"
                  label="ICAO"
                  parse={(value: string) => {
                    if (!initialValues._id && value?.length === 4) {
                      handleCheckForAirports("Ident", value);
                    }
                    return value?.toUpperCase();
                  }}
                  name="Ident"
                />
                <FormikTextField
                  className="field textfield airport loc"
                  label="Latitude (dec°)"
                  name="Loc.coordinates[1]"
                  placeholder="example: 44.881111"
                />
                <FormikTextField
                  className="field textfield airport loc"
                  label="Longitude (dec°)"
                  name="Loc.coordinates[0]"
                  placeholder="example: -63.508611"
                />
                <FormikSelectField
                  name="timezoneId"
                  label="Timezone"
                  format={(value) => timezoneOptions.find((tz) => tz.value === value)}
                  parse={(option) => option.value}
                  options={timezoneOptions}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  isSearchable={true}
                  isClearable={false}
                  placeholder=""
                />
                <FormikTextField
                  className="field textfield airport country"
                  label="Country Code"
                  name="CountryCode"
                  parse={(value: string) => value?.toUpperCase()}
                  placeholder="example: CY"
                />
              </Grid>
              <Grid item xs={6}>
                <FormikTextField
                  className="field textfield airport elevation"
                  label="Elevation (ft msl)"
                  name="ElevationFeet"
                />
                <FormikTextField
                  className="field textfield airport taxiout"
                  label="Default Taxi Out (min)"
                  name="defaultTaxiOutMinutes"
                  inputProps={{ type: "number", min: 0 }}
                  parse={parseTaxiMinutes}
                  placeholder="Uses customer default if blank"
                />
                <FormikTextField
                  className="field textfield airport taxiin"
                  label="Default Taxi In (min)"
                  name="defaultTaxiInMinutes"
                  inputProps={{ type: "number", min: 0 }}
                  parse={parseTaxiMinutes}
                  placeholder="Uses customer default if blank"
                />
                <FormikTextField
                  className="field textfield airport fir"
                  label="Flight Information Region"
                  name="FIR"
                  parse={(value: string) => value?.toUpperCase()}
                  placeholder="example: MONCTON"
                />
                <FormikTextField
                  className="field textfield airport code"
                  label="IATA"
                  parse={(value: string) => {
                    if (!initialValues._id && value?.length === 3) {
                      handleCheckForAirports("IATACode", value);
                    }
                    return value?.toUpperCase();
                  }}
                  name="IATACode"
                />
                <CustomFieldsV2
                  schema={schema}
                  objectPath={customPath}
                  resource={PolicyResource.Airport}
                  displayHeader={false}
                />
                {selectedAirport?.Ident && (
                  // Key on the ident so switching airports remounts this section -
                  // otherwise local state (dirty flag, in-progress edits) would leak
                  // across airports.
                  <AirportGendecSection key={selectedAirport.Ident} airportIdent={selectedAirport.Ident} />
                )}
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => setShowGateModal(true)}
                  sx={{ mt: 1 }}
                >
                  Gate Management ({airportForm.values.gates?.length ?? 0} total)
                </Button>
              </Grid>
              <Grid item xs={12}>
                <FormikRichTextField
                  className="field richtextfield airport note"
                  label="Note"
                  name="note"
                  templateHtml={templateHtml}
                />
              </Grid>
              <Grid item xs={12} sx={{ pt: 2 }}>
                <ButtonGroup variant="contained" size="small">
                  <Button
                    type="reset"
                    color="warning"
                    disabled={(!dirty && isValid) || isSubmitting}
                    onClick={() => {
                      setICAOMatch("");
                      setIATAMatch("");
                      handleReset();
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    color="success"
                    disabled={isSubmitting || !isValid || !dirty || !!ICAOMatch || !!IATAMatch}
                    type="submit"
                  >
                    {selectedAirport?.createdAt ? "Update" : "Create"}
                  </Button>

                  <Button
                    style={{ marginRight: 0, marginLeft: "auto" }}
                    className={"btn preview"}
                    disabled={!values || isSubmitting}
                    onClick={() => openAirportInfoWindow(values)}
                  >
                    Preview in Info Window
                  </Button>
                </ButtonGroup>
              </Grid>
            </Grid>
            <GateManagement
              parentForm={airportForm}
              showGateModal={showGateModal}
              setShowGateModal={setShowGateModal}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
